/**
 * Phase H — "Send to Prompt Lab" wire-up helper.
 *
 * Creates a fresh PromptSession in Redux that's primed with the document
 * as context, then returns the session id so the caller can route the
 * Prompt Lab window via WindowContext to focus it.
 *
 * Why a helper (not inlined in DocumentPreview): the same flow could be
 * triggered from a future right-click "Send to Prompt Lab" item or from
 * the corpus-search results — keeping the dispatch logic in one module
 * avoids the four-call divergence that bit Phase G.
 *
 * Tone of the prefilled prompts follows Q10's "respectful + lawyer-savvy"
 * guidance: address the assistant as a colleague, not a butler.
 */
import type { AnyAction, Dispatch } from '@reduxjs/toolkit';
import {
  addSession,
  brandId,
  PromptSessionStatus,
  addDocumentContext,
  upsertDocument,
  DocumentType,
  ConfidentialityLevel,
  type PromptSession,
  type PromptSessionId,
  type DocumentId,
  type MatterId,
  type Document,
} from '@repancy/core';

export interface PromptLabPrefillInput {
  docId: string;
  docTitle: string;
  docContent: string;
  /**
   * Optional doc metadata so the Document-Context chip can render the
   * doc-type pill + confidentiality badge. When omitted, sensible
   * defaults are used (Memo / Confidential). Without this metadata
   * `DocumentContext.tsx` looks up `s.documents.entities[id]`, finds
   * nothing, and renders "No documents attached" — the canonical
   * "I sent to Prompt Lab but nothing shows up" bug.
   */
  matterId?: string;
  documentType?: DocumentType;
  confidentialityLevel?: ConfidentialityLevel;
  responsibleAttorneyId?: string;
  modifiedAt?: string;
  /**
   * Optional "Send to Prompt Lab" suggestion chips. Phase G's Prompts
   * folder seeds 5 prompt templates; the desktop UI passes them in.
   * Each entry becomes a userPrompt suggestion (first entry primes the
   * editor; the rest sit in a chip strip the editor renders).
   */
  suggestedPrompts?: ReadonlyArray<{ name: string; body: string }>;
}

export interface PromptLabPrefillResult {
  sessionId: PromptSessionId;
  /** Stable path so the WindowContext caller can `openWindow(path)`. */
  windowPath: string;
}

const PROMPT_LAB_PATH = '/prompt-lab';

/**
 * Compose the system prompt — short context block plus a clear scope
 * marker so the model treats the doc as the corpus to reason about.
 */
function composeSystemPrompt(docTitle: string, docContent: string): string {
  const body = docContent.trim().length > 0 ? docContent : '(empty document body)';
  return [
    'You are a legal-aware drafting assistant working with a Repancy lawyer.',
    '',
    'The user has loaded the following document as the active context. Treat it',
    'as the subject of the conversation unless they explicitly redirect you.',
    '',
    `=== DOCUMENT: ${docTitle} ===`,
    body,
    `=== END DOCUMENT ===`,
  ].join('\n');
}

/**
 * Default user prompt that arrives when the lawyer clicks "Send to Prompt
 * Lab" without picking a suggestion. Per Q11 locked text: a clear,
 * lawyer-respectful starter that doesn't presume an outcome.
 *
 * Doc-aware so the lawyer sees *which* document was attached without
 * scrolling the system prompt or hunting for the chip strip.
 */
function composeUserPrompt(docTitle: string): string {
  const trimmed = docTitle.trim();
  return trimmed.length > 0
    ? `Summarize "${trimmed}".`
    : 'Summarize this document.';
}

/**
 * Dispatches a fresh Prompt Lab session keyed off the supplied doc.
 *
 * Pure-Redux only — no Convex writes, no window-open. The caller wires
 * the WindowContext open/focus in the same click handler (split so the
 * vitest can pin the Redux contract without needing a window provider).
 */
export function dispatchPromptLabPrefill(
  dispatch: Dispatch<AnyAction>,
  input: PromptLabPrefillInput,
): PromptLabPrefillResult {
  const sessionId = brandId<'PromptSession'>(`session-${Date.now()}`) as PromptSessionId;
  const now = new Date().toISOString();

  const session: PromptSession = {
    id: sessionId,
    systemPrompt: composeSystemPrompt(input.docTitle, input.docContent),
    userPrompt: input.suggestedPrompts?.[0]?.body ?? composeUserPrompt(input.docTitle),
    skillPrompts: [],
    modelId: 'anthropic-sonnet-4',
    documentContextIds: [],
    status: PromptSessionStatus.Idle,
    createdAt: now,
    userId: '',
  };

  dispatch(addSession(session));

  // 2026-05-27 — upsert the document entity FIRST so the
  // DocumentContext chip resolves. DocumentContext.tsx does
  //   attachedDocs = documentIds.map(id => documentEntities[id]).filter(Boolean)
  // — without this upsert the chip silently disappears ("No documents
  // attached" even though the id was added to the session). Sensible
  // defaults cover callers that don't have the full metadata handy
  // (TreePane mock rows, remote driveItems, corpus-search results).
  const docEntity: Document = {
    id: input.docId as unknown as DocumentId,
    matterId: (input.matterId ?? '') as unknown as MatterId,
    title: input.docTitle || '(untitled)',
    content: input.docContent,
    documentType: input.documentType ?? DocumentType.Memo,
    confidentialityLevel:
      input.confidentialityLevel ?? ConfidentialityLevel.Confidential,
    responsibleAttorneyId: input.responsibleAttorneyId ?? '',
    currentVersion: 1,
    versionHistory: [],
    createdAt: input.modifiedAt ?? now,
    modifiedAt: input.modifiedAt ?? now,
  };
  dispatch(upsertDocument(docEntity));

  // Attach the doc as a structured context entry too (the chips in
  // DocumentContext.tsx render from this list). Use the raw `docId`
  // because Phase D's DocumentContext component treats the ids
  // opaquely.
  dispatch(
    addDocumentContext({
      sessionId: sessionId as unknown as string,
      documentId: input.docId as unknown as DocumentId,
    }),
  );

  return { sessionId, windowPath: PROMPT_LAB_PATH };
}

export { PROMPT_LAB_PATH };
